<template>
  <div id="patient-calling-modal"
       class="custom-modal"
       v-if="showCallModal"
       :title="$t('call_noun')">
    <div class="text-right">
      <a class="close-svg"
         @click="closeCustomModal">
        <CloseSvg/>
      </a>
    </div>
    <div class="pt-lg-2">
      <div>
        <span class="mr-2">{{ $t('call_from_phone') }}: {{ phone }}</span>
        (<a class="text-primary pointer"
           v-if="!copied"
           @click="copyPhoneToClipboard">{{ $t('copy_to_clipboard') }}</a>
        <a v-else>{{ $t('copied') }}</a>)
      </div>
      <div v-if="patients && patients.length > 1">
        {{ $t('patient') }}:
        <select v-model="patient"
                class="form-control w-auto pr-5 d-inline-block py-0 ml-2"
                style="height: 34px;">
          <option v-for="p in patients"
                  :value="p"
                  :key="`p-${p.id}`">
            {{ p.full_name }}
          </option>
        </select>
      </div>
      <div v-else-if="patient">
        {{ $t('patient') }}: {{ patient.full_name }}
        <PatientTags class="ml-2"
                     :patient="patient"/>
      </div>
      <div v-else>{{ $t('patient_not_derived') }}</div>
    </div>
    <div class="pt-4 pt-lg-5">
      <div class="modal-footer-buttons no-print pl-lg-5">
        <button class="btn-themed btn-themed-squared btn-themed-outline d-inline-block d-sm-inline-block align-middle"
                @click="toCalendar">
          {{ $t('record_on_visit') }}
        </button>
        <button class="ml-3 btn-themed btn-themed-squared btn-themed-outline w-auto px-3 d-inline-block d-sm-inline-block align-middle"
                @click="createTask">
          {{ $t('create_task') }}
        </button>
        <button class="ml-3 btn-themed btn-themed-squared btn-themed-outline w-auto px-3 d-inline-block d-sm-inline-block align-middle"
                @click="createLead">
          {{ $t('create_lead') }}
        </button>
        <button v-if="!patient"
                class="ml-3 btn-themed btn-themed-squared w-auto px-3 d-inline-block d-sm-inline-block align-middle"
                @click="addPatient">
          {{ $t('add_patient') }}
        </button>
        <button :disabled="loading"
                v-if="patient"
                @click="moveToCard"
                class="ml-3 w-auto px-3 btn-themed align-middle d-inline-block d-sm-inline-block btn-themed-squared">
          {{ $t('patient_card') }}
        </button>
      </div>
    </div>
  </div>

</template>

<script setup>
import moment from "moment/moment"
import CloseSvg from '@/assets/svg-vue/general/close.svg'
import PatientTags from "@/components/parts/patients/PatientTags.vue"
import { copyObject } from "@/extensions/prototypes/prototypes"
import { computed, nextTick, ref } from "vue"
import { useStore } from "vuex"
import { useRouter } from "vue-router"

const props = defineProps({
  patients: {},
  phone: {},
  leadId: {},
})

const store = useStore()
const router = useRouter()

const copied = ref(false)
const patient = ref(null)

const branch = computed(() => store.state.auth.branch)
const loading = computed(() => store.state.dom.loading)
const showCallModal = computed(() => store.state.crm.showCallModal)

// navigator.clipboard is missing on non-secure origins and writeText can be rejected (no permission):
// copying is a convenience and must not stop the action it is part of
const copyPhone = async () => {
  try {
    await navigator.clipboard.writeText(props.phone)
    return true
  } catch (e) {
    console.log(e)
    return false
  }
}

const toCalendar = () => {
  copyPhone()
  if(patient.value) {
    store.commit('setPatientFromCall', copyObject(patient.value))
  }
  store.commit('setLeadForRecord', props.leadId ? { id: props.leadId } : null)
  store.commit('setPhoneFromCall', props.phone)
  router.push({
    name: 'Calendar',
  }).catch(()=>{})
  store.commit('setShowCallModal', false)
}

const createTask = () => {
  copyPhone()
  store.commit('setShowModal', false)
  nextTick(() => {
    store.commit('setViewModel', 'task')
    store.commit('setModule', 'crm')
    store.commit('setSelectedModel', {
      phone: props.phone,
      patient_id: patient.value ? patient.value.id : null,
      patient: patient.value ?? null,
      task_type: 'task',
      start: moment().format('YYYY-MM-DD'),
      deadline: moment().format('YYYY-MM-DD')
    })
    store.commit('setShowModal', true)
  })
  store.commit('setShowCallModal', false)
}

const createLead = () => {
  copyPhone()
  store.commit('setShowModal', false)
  nextTick(() => {
    store.commit('setViewModel', 'lead')
    store.commit('setModule', 'crm')
    store.commit('setSelectedModel', {
      phone: props.phone,
      patient_id: patient.value ? patient.value.id : null,
      fname: patient.value ? patient.value.fname : null,
      lname: patient.value ? patient.value.lname : null,
      patient: patient.value ?? null,
    })
    store.commit('setShowModal', true)
  })
  store.commit('setShowCallModal', false)
}

const addPatient = () => {
  store.commit('setShowModal', false)
  nextTick(() => {
    store.commit('setViewModel', 'patient')
    store.commit('setModule', 'patients')
    store.commit('setSelectedModel', {
      branch_id: branch.value,
      mphone: props.phone
    })
    store.commit('setShowModal', true)
  })
  store.commit('setShowCallModal', false)
}

const moveToCard = () => {
  if(!patient.value) return
  store.commit('setShowCallModal', false)
  router.push({
    name: 'PatientVisits',
    params: {
      id: patient.value.id,
    }
  }).catch(()=>{})
}

const copyPhoneToClipboard = async () => {
  copied.value = await copyPhone()
}

const closeCustomModal = () => {
  store.commit('setShowCallModal', false)
  copied.value = false
}

// called by LaravelEcho (ref) on an incoming call: preselects the first found patient
const show = () => {
  if(props.patients && props.patients.length) {
    patient.value = props.patients[0]
  } else {
    patient.value = null
  }
}

defineExpose({ show })
</script>

<style scoped lang="scss">
.custom-modal {
  position: fixed;
  padding: 36px;
  border-radius: 24px;
  box-shadow: 0px 4px 35px -14px rgba(92, 92, 102, 0.38);
  right: 24px;
  bottom: 24px;
  z-index: 30;
  background: #fff;
  .close-svg {
    svg {
      width: 16px;
      height: 16px;
      fill: $gray-text;
      cursor: pointer;
      opacity: 0.7;
    }
    &:hover {
      svg {
        opacity: 1;
      }
    }
  }
}
@media(max-width: 992px) {
  .custom-modal {
    padding: 20px;
  }
}
</style>
